import Image from 'next/image';
import { useMemo } from 'react';

import { KeyValueRow, KeyValueTable, Text } from '@/design-system/components/ui';
import useGetChainInfo from '@/hooks/useGetChainInfo';

import { Trigger } from '../../domain';
import { AddressChip } from './components/AddressChip';
import { CurrencyDisplay } from './components/CurrencyDisplay';

interface Props {
  trigger: Trigger;
}

export function PendleLPTriggerFullDetails({ trigger }: Props) {
  const { getChainIconUrl } = useGetChainInfo();

  const networkDisplay = useMemo(() => {
    if (!trigger.contracts?.[0]) return '-';

    const contract = trigger.contracts[0];
    const networkName = contract.network;
    const chainIconUrl = getChainIconUrl(contract.chainId);

    return (
      <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
        {chainIconUrl && <Image src={chainIconUrl} alt={networkName ?? 'chain'} width={20} height={20} />}
        <span style={{ textTransform: 'capitalize' }}>{networkName}</span>
      </div>
    );
  }, [trigger.contracts, getChainIconUrl]);

  const contractDisplay = useMemo(() => {
    if (!trigger.contracts?.[0]) return '-';

    const contract = trigger.contracts[0];

    return (
      <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
        <Text as="span" variant="body-sm" color="muted">
          Snapshots of Pendle points calculations for contract
        </Text>
        <AddressChip address={contract.address} chainId={contract.chainId} />
      </div>
    );
  }, [trigger.contracts]);

  const volumeCurrencyDisplay = useMemo(() => {
    if (!trigger.currencyVolumeExpression) return '-';

    const chainId = trigger.contracts?.[0]?.chainId;
    return <CurrencyDisplay value={trigger.currencyVolumeExpression} chainId={chainId} />;
  }, [trigger.currencyVolumeExpression, trigger.contracts]);

  return (
    <KeyValueTable>
      <KeyValueRow label="Name" value={trigger.name} />
      <KeyValueRow label="Description" value={trigger.description} />
      <KeyValueRow label="Executed with" value={contractDisplay} />
      <KeyValueRow label="Network" value={networkDisplay} />
      <KeyValueRow label="Volume currency" value={volumeCurrencyDisplay} />
      <KeyValueRow label="Argument conditions" value={trigger.conditionExpression ?? '-'} />
    </KeyValueTable>
  );
}
